import { Component, Input, signal, SimpleChanges } from '@angular/core';
import { GetSystemConfigViewModel } from 'edu-application/dist/system-config/get-system-config/get-system-config.veiwmodel';
import { NotificationChannelMode } from 'edu-core/dist/entities/system-config/system-config';
import { SettingsService } from '../settings.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PermissionsService } from '../../shared/permissions/permissions.service';
import { PERMISSIONS } from 'edu-core';

interface ModeOption {
  id: NotificationChannelMode;
  label: string;
  sub: string;
}

@Component({
  selector: 'edu-notification-settings',
  standalone: false,
  templateUrl: './notification-settings.component.html',
  styleUrl: './notification-settings.component.scss',
})
export class NotificationSettingsComponent {
  @Input() orgId!: string;
  @Input() systemConfig: GetSystemConfigViewModel | null = null;

  readonly options: ModeOption[] = [
    { id: 'off', label: 'Off', sub: 'No notifications' },
    { id: 'email', label: 'Email Only', sub: 'Gmail' },
    { id: 'whatsapp', label: 'WhatsApp Only', sub: 'WhatsApp' },
    { id: 'both', label: 'Both', sub: 'Email + WhatsApp' },
  ];

  missedDeadline = signal<NotificationChannelMode>('both');
  absence = signal<NotificationChannelMode>('both');
  isSaving = signal(false);

  constructor(
    private settingsService: SettingsService,
    private snackBar: MatSnackBar,
    private permissions: PermissionsService,
  ) {}

  /** Writing these settings requires organization-settings.manage. */
  get canManage(): boolean {
    return this.permissions.has(PERMISSIONS.organizationSettings.manage);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['systemConfig'] && this.systemConfig) {
      this.missedDeadline.set(this.systemConfig.missedDeadlineNotifications);
      this.absence.set(this.systemConfig.absenceNotifications);
    }
  }

  selectMissedDeadline(mode: NotificationChannelMode): void {
    if (!this.canManage || this.isSaving() || this.missedDeadline() === mode) {
      return;
    }
    const previous = this.missedDeadline();
    this.missedDeadline.set(mode);
    this.save(previous, this.absence());
  }

  selectAbsence(mode: NotificationChannelMode): void {
    if (!this.canManage || this.isSaving() || this.absence() === mode) {
      return;
    }
    const previous = this.absence();
    this.absence.set(mode);
    this.save(this.missedDeadline(), previous);
  }

  /** Saves both settings; on failure reverts to the previous values. */
  private save(
    previousMissedDeadline: NotificationChannelMode,
    previousAbsence: NotificationChannelMode,
  ): void {
    this.isSaving.set(true);
    this.settingsService
      .setNotificationSettings(this.orgId, this.missedDeadline(), this.absence())
      .subscribe({
        next: () => {
          this.snackBar.open('✅ Notification settings updated', 'Dismiss', {
            duration: 3000,
          });
          this.isSaving.set(false);
        },
        error: () => {
          this.missedDeadline.set(previousMissedDeadline);
          this.absence.set(previousAbsence);
          this.snackBar.open(
            '❌ Failed to update notification settings',
            'Dismiss',
            { duration: 3000 },
          );
          this.isSaving.set(false);
        },
      });
  }
}
